import { Box, Button, HStack, IconButton, Link, Menu, Portal, Text, VStack } from '@chakra-ui/react'
import { FiChevronDown, FiPhone } from 'react-icons/fi'
import { callChannelLabel, callHref, useCallChannels, type CallChannel } from '../../lib/callChannel'
import { formatPhone } from '../../lib/contactMethods'

export interface CallButtonProps {
  phone: string | null
  /** When set, renders a disabled button showing this text (e.g. "Do not contact"). */
  disabledReason?: string
  onCallStarted?: (c: CallChannel) => void
  /** 'lg' = canvasser touch target (>= 48px). */
  size?: 'sm' | 'lg'
}

/** The one place a call is placed. Renders the default channel as the primary action. */
export function CallButton({ phone, disabledReason, onCallStarted, size = 'sm' }: CallButtonProps) {
  const { channels, defaultChannel } = useCallChannels()
  const minH = size === 'lg' ? '48px' : '44px'
  const others = channels.filter(c => c !== defaultChannel)

  if (!phone || disabledReason) {
    return (
      <Button size={size === 'lg' ? 'lg' : 'sm'} variant="outline" disabled minH={minH}>
        <FiPhone />
        <Text as="span">{disabledReason ?? 'No phone'}</Text>
      </Button>
    )
  }

  const href = callHref(defaultChannel, phone)

  return (
    <HStack gap={1} flexWrap="wrap">
      <Button
        asChild
        size={size === 'lg' ? 'lg' : 'sm'}
        colorPalette="brand"
        minH={minH}
        minW={minH}
      >
        <Link
          href={href ?? undefined}
          onClick={(e) => { e.stopPropagation(); onCallStarted?.(defaultChannel) }}
          textDecoration="none"
        >
          <FiPhone />
          <VStack gap={0} align="start">
            <Text as="span" lineHeight="short">Call</Text>
            <Text as="span" fontSize="xs" fontWeight="normal" lineHeight="short">{formatPhone(phone)}</Text>
          </VStack>
        </Link>
      </Button>
      {others.length > 0 && (
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Other ways to call"
              variant="outline"
              size={size === 'lg' ? 'lg' : 'sm'}
              minW={minH}
              minH={minH}
              onClick={(e) => e.stopPropagation()}
            >
              <FiChevronDown />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content onClick={(e) => e.stopPropagation()}>
                {others.map(c => {
                  const h = callHref(c, phone)
                  return (
                    <Menu.Item
                      key={c}
                      value={c}
                      disabled={!h}
                      onSelect={() => {
                        if (!h) return
                        onCallStarted?.(c)
                        window.location.href = h
                      }}
                    >
                      <Box>{callChannelLabel(c)}</Box>
                    </Menu.Item>
                  )
                })}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      )}
    </HStack>
  )
}
